/**
 * @file API.ts - S3 Storage Integration
 *
 * This module provides the API layer for S3 storage operations.
 * All operations require JWT authentication - the token is retrieved
 * from localStorage and sent as a Bearer token in the Authorization header.
 *
 * The backend proxies these requests to AWS S3, using the JWT to:
 * 1. Verify user identity
 * 2. Scope operations to user's namespace (prevents cross-user access)
 * 3. Apply rate limiting and quotas
 *
 * Available operations:
 * - checkFileExists: Check if a file exists in S3
 * - uploadFile: Upload/save an Excalidraw scene to S3
 * - listFiles: List user's files in S3
 * - listObjects: List files and folders with optional prefix filtering
 * - getFile: Download/retrieve a file from S3
 * - deleteObject: Delete a file or folder from S3
 * - createFolder: Create a new folder in S3
 *
 * Security: All API calls validate JWT on the backend before
 * performing any S3 operations.
 */
import { ExcalidrawError } from "@excalidraw/excalidraw/errors";

/**
 * Base URL for S3 API endpoints.
 * Points to the backend S3 proxy service.
 */
const API_URL = `${import.meta.env.VITE_APP_HTTP_BACKEND_URL}s3/`;

/**
 * Checks if a file exists in the user's S3 bucket.
 *
 * @param key - Full S3 object key (path + filename)
 * @returns true if file exists, false otherwise
 * @throws ExcalidrawError if JWT token is missing
 */
export async function checkFileExists(key: string) {
  const JWT = localStorage.getItem("access_token");
  if (!JWT) {
    throw new ExcalidrawError("No JWT token found");
  }
  const res = await fetch(`${API_URL}check/${key}`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${JWT}`,
    },
  });

  if (!res.ok) {
    return false;
  }

  const exist = await res.json();
  return exist;
}

/**
 * Uploads a file (typically serialized Excalidraw scene) to S3.
 * Handles path/filename separation and sends to backend for storage.
 *
 * @param fileName - Full path including filename (e.g., "folder/subfolder/drawing.excalidraw")
 * @param serialized - Serialized Excalidraw scene (JSON string or object)
 * @returns Success status from the upload operation
 * @throws ExcalidrawError if JWT missing or upload fails
 */
export async function uploadFile(
  fileName: string,
  serialized: string | object,
) {
  const JWT = localStorage.getItem("access_token");
  if (!JWT) {
    throw new ExcalidrawError("No JWT token found");
  }
  // separate prefix (folder path) and fileName
  const lastSlashIndex = fileName.lastIndexOf("/");
  const prefix =
    lastSlashIndex !== -1 ? fileName.substring(0, lastSlashIndex + 1) : "";
  const fileOnlyName =
    lastSlashIndex !== -1 ? fileName.substring(lastSlashIndex + 1) : fileName;

  const res = await fetch(`${API_URL}${fileOnlyName}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${JWT}`,
    },
    body: JSON.stringify({
      serialized,
      prefix,
    }),
  });
  if (!res.ok) {
    throw new ExcalidrawError("Failed to upload file");
  }
  const data = await res.json();
  return data.success;
}

/**
 * Lists all files in the user's S3 workspace.
 * Returns flat list of file keys.
 *
 * @returns Array of file keys
 * @throws ExcalidrawError if JWT missing or request fails
 */
export async function listFiles() {
  const JWT = localStorage.getItem("access_token");
  if (!JWT) {
    throw new ExcalidrawError("No JWT token found");
  }
  const res = await fetch(API_URL, {
    method: "GET",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${JWT}`,
    },
  });

  if (!res.ok) {
    const error = await res.json();
    throw new ExcalidrawError(`Failed to list files: ${error.message}`);
  }

  const files = await res.json();
  return files;
}

/**
 * Lists files and folders in S3 with optional prefix filtering.
 * Used by S3Browser for hierarchical navigation.
 *
 * @param prefix - Folder path prefix (e.g., "myFolder/" for contents of myFolder)
 * @returns Object with 'files' and 'folders' arrays
 * @throws ExcalidrawError if JWT missing or request fails
 */
export async function listObjects(prefix: string) {
  const JWT = localStorage.getItem("access_token");
  if (!JWT) {
    throw new ExcalidrawError("No JWT token found");
  }
  const uri = `${API_URL}all${
    prefix ? `?prefix=${encodeURIComponent(prefix)}` : ""
  }`;

  const res = await fetch(uri, {
    method: "GET",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${JWT}`,
    },
  });

  if (!res.ok) {
    const error = await res.json();
    throw new ExcalidrawError(`Failed to list files: ${error.message}`);
  }

  const files = await res.json();
  return files;
}

/**
 * Retrieves a file from S3 by its key.
 * Used for loading Excalidraw scenes from S3 storage.
 *
 * @param key - Full S3 object key (path + filename)
 * @returns File contents as JSON string
 * @throws ExcalidrawError if JWT missing or file not found
 */
export async function getFile(key: string) {
  const JWT = localStorage.getItem("access_token");
  if (!JWT) {
    throw new ExcalidrawError("No JWT token found");
  }

  const lastSlashIndex = key.lastIndexOf("/");
  const prefix =
    lastSlashIndex !== -1 ? key.substring(0, lastSlashIndex + 1) : "";
  const fileOnlyName =
    lastSlashIndex !== -1 ? key.substring(lastSlashIndex + 1) : key;

  const res = await fetch(
    `${API_URL}${fileOnlyName}${
      prefix ? `?prefix=${encodeURIComponent(prefix)}` : ""
    }`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${JWT}`,
      },
    },
  );

  if (!res.ok) {
    throw new ExcalidrawError("Failed to fetch file");
  }
  const file = await res.json();

  return JSON.stringify(file);
}

/**
 * Deletes a file or folder from S3.
 * For folders, includes recursive deletion of contents.
 *
 * @param key - S3 object key to delete (append '/' for folders)
 * @throws ExcalidrawError if JWT missing or deletion fails
 */
export async function deleteObject(key: string) {
  const JWT = localStorage.getItem("access_token");
  if (!JWT) {
    throw new ExcalidrawError("No JWT token found");
  }
  const res = await fetch(`${API_URL}`, {
    method: "DELETE",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${JWT}`,
    },
    body: key,
  });

  if (!res.ok) {
    const error = await res.json();
    throw new ExcalidrawError(`Failed to delete object: ${error.message}`);
  }
}

/**
 * Creates a new folder in user's S3 workspace.
 * Creates an empty placeholder object with trailing slash.
 *
 * @param folderName - Folder path to create (without trailing slash)
 * @throws ExcalidrawError if JWT missing or creation fails
 */
export async function createFolder(folderName: string) {
  const JWT = localStorage.getItem("access_token");
  if (!JWT) {
    throw new ExcalidrawError("No JWT token found");
  }
  const res = await fetch(`${API_URL}folder/post`, {
    method: "POST",
    headers: {
      "Content-Type": "text/plain",
      Authorization: `Bearer ${JWT}`,
    },
    body: folderName,
  });

  if (!res.ok) {
    const error = await res.json();
    throw new ExcalidrawError(`Failed to create folder: ${error.message}`);
  }
}
